/**
 * ForgeWarden.ts
 * Hammer-and-shield mech miniboss for World 4 (Crystal Tide Ruins).
 *
 * Behaviour: a slow, armoured mech that advances on Nova with shield
 * raised. Every `GUARD_DURATION` seconds it vents superheated steam
 * for `VENT_DURATION` — shield down, vulnerable to shards and stomps
 * during the vent window. Outside the vent every hit reads as
 * `BLOCKED` / `false`. Defeated on the third successful vent-window hit.
 *
 * State machine: GUARDING → VENTING → GUARDING
 *                 (with HP=0 at any VENTING hit transitioning to DEAD)
 *
 * Rules (AGENTS.md):
 *   - No React imports. No async. No Skia imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 4 miniboss greybox stub. Wired into levels when W4 ships.
 * See ROADMAP.md Phase 4 (`ForgeWarden.ts`).
 *
 * NOTE: The hammer-attack hitbox is intentionally not implemented yet — the
 * level combat layer will hook a melee strike onto Nova once W4 levels exist.
 * The greybox covers the headline "steam stun windows" mechanic only.
 *
 * @module ForgeWarden
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import { resolveAllPlatforms, hasFloorAhead } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type ForgeWardenState = 'GUARDING' | 'VENTING' | 'DEAD';

/**
 * Projectile-impact result. `BLOCKED` is the default while GUARDING;
 * `HURT` only fires inside the VENTING window.
 */
export type ForgeWardenHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/** Width/height of the Warden hitbox in logical pixels — larger than a regular mob. */
export const FORGE_WARDEN_WIDTH = 40;
export const FORGE_WARDEN_HEIGHT = 36;
/** Greybox render colour — iron blue. */
export const FORGE_WARDEN_COLOR = '#3a4858';

/** HP pool — three connected vent-window hits to kill. */
export const FORGE_WARDEN_MAX_HP = 3;

/** Slow advance speed in logical pixels per second. */
const GUARD_SPEED = 50;
/** Time spent shielded between vents. */
const GUARD_DURATION = 4.5;
/** Time spent vulnerable while venting. */
const VENT_DURATION = 1.5;

// DECISION: Reusable hitbox object to avoid per-tick allocations in the
// engine hot path (mirrors Gearling / BrambleRam / SparkImp).
const _hitbox = { x: 0, y: 0, width: FORGE_WARDEN_WIDTH, height: FORGE_WARDEN_HEIGHT };

/**
 * ForgeWarden — World 4 miniboss (Crystal Tide Ruins).
 *
 * Spawn with `new ForgeWarden(x, y)`. Call `update(dt, level, nova?)` each
 * fixed tick; pass the latest Nova snapshot so the Warden advances toward
 * her. Read `dead` to know when to remove from the scene; read `hp` for
 * HUD purposes.
 */
export class ForgeWarden extends BaseEntity {
  public readonly entityType = 'ForgeWarden';
  public readonly color = FORGE_WARDEN_COLOR;
  public readonly maxHp = FORGE_WARDEN_MAX_HP;
  public state: ForgeWardenState = 'GUARDING';

  /**
   * True once dead. ForgeWarden has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  /** Remaining HP. Decremented only by hits landed during VENTING. */
  public hp = FORGE_WARDEN_MAX_HP;
  /** True when facing right (used by renderer for sprite flip). */
  public facingRight = true;
  private _guardTimer = GUARD_DURATION;
  private _ventTimer = 0;
  /**
   * True while standing on solid ground. Tracked explicitly because `vy` is
   * not usable as a grounded test inside the state switch — gravity is applied
   * before it runs, so `vy` is always non-zero there even when the Warden is
   * resting on a platform.
   */
  private _grounded = false;

  constructor(x: number, y: number) {
    super(x, y, FORGE_WARDEN_WIDTH, FORGE_WARDEN_HEIGHT);
  }

  /**
   * Advance the Warden by one fixed tick.
   * @param dt    Fixed-step delta in seconds.
   * @param level Current level geometry — read-only.
   * @param nova  Optional Nova runtime snapshot. The Warden faces and advances
   *              on Nova when available; without one it stands still.
   */
  public update(dt: number, level: LevelRuntime, nova?: NovaRuntime): void {
    if (this.state === 'DEAD') return;

    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    switch (this.state) {
      case 'GUARDING':
        this._driveAdvance(nova, level);
        this._integrateAndResolve(dt, level);
        this._guardTimer -= dt;
        if (this._guardTimer <= 0) this._enterVenting();
        break;
      case 'VENTING':
        this.vx = 0;
        this._integrateAndResolve(dt, level);
        this._ventTimer -= dt;
        if (this._ventTimer <= 0) this._enterGuarding();
        break;
    }
  }

  /**
   * Walk slowly toward Nova when a snapshot is available; otherwise hold
   * position. Locks `facingRight` to the side Nova is on so the renderer
   * can flip the sprite.
   *
   * DECISION: at a ledge the Warden STOPS rather than turning around. The
   * patroller enemies reverse at an edge because their whole behaviour is a
   * back-and-forth sweep, but the Warden is a chaser — reversing would send it
   * walking away from the player, which reads as a bug rather than a guard
   * holding a line. It keeps facing Nova so the sprite stays correct and it
   * resumes the moment there is floor ahead again.
   *
   * @param nova - Latest Nova snapshot, or undefined when none is available.
   * @param level - Level geometry, used for the ledge probe.
   */
  private _driveAdvance(nova: NovaRuntime | undefined, level: LevelRuntime): void {
    if (!nova) {
      this.vx = 0;
      return;
    }
    if (nova.x >= this.x) {
      this.vx = GUARD_SPEED;
      this.facingRight = true;
    } else {
      this.vx = -GUARD_SPEED;
      this.facingRight = false;
    }
    if (
      this._grounded &&
      !hasFloorAhead(this.x, this.y, this.width, this.height, this.vx, level.platforms)
    ) {
      this.vx = 0;
      Logger.debug('ForgeWarden', 'Hold at ledge');
    }
  }

  /**
   * Integrate position + velocity and resolve platform overlaps.
   */
  private _integrateAndResolve(dt: number, level: LevelRuntime): void {
    this.x += this.vx * dt;
    this.y += this.vy * dt;
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const r = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    this.x = r.x;
    this.y = r.y;
    if (r.landedOnTop) this.vy = 0;
    this._grounded = r.landedOnTop;
  }

  private _enterVenting(): void {
    this.state = 'VENTING';
    this._ventTimer = VENT_DURATION;
    this.vx = 0;
    Logger.debug('ForgeWarden', 'Steam vent → VENTING (shield down)');
  }

  private _enterGuarding(): void {
    this.state = 'GUARDING';
    this._guardTimer = GUARD_DURATION;
    Logger.debug('ForgeWarden', 'Shield up → GUARDING');
  }

  /**
   * Apply one projectile hit.
   * @returns `'BLOCKED'` while GUARDING, `'HURT'` while VENTING,
   *          `'IGNORED'` once DEAD.
   */
  public applyShardHit(): ForgeWardenHitResult {
    if (this.state === 'DEAD') return 'IGNORED';
    if (this.state !== 'VENTING') return 'BLOCKED';
    this._takeDamage();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. Armoured from above while GUARDING.
   * @returns `true` when the stomp connected (HP decremented); `false` otherwise.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD') return false;
    if (this.state !== 'VENTING') return false;
    this._takeDamage();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts).
   * @param fromStomp `true` for stomp contact, `false`/omitted for shards.
   * @returns `true` if this hit dealt damage; `false` if blocked or ignored.
   */
  public applyHit(fromStomp = false): boolean {
    if (fromStomp) return this.applyStomp();
    return this.applyShardHit() === 'HURT';
  }

  /**
   * Deduct one HP and route to DEAD when the pool empties. Called only
   * from a successful VENTING-state hit.
   */
  private _takeDamage(): void {
    this.hp -= 1;
    Logger.debug('ForgeWarden', `Hit landed — HP ${this.hp}/${this.maxHp}`);
    if (this.hp <= 0) {
      this.state = 'DEAD';
      this.dead = true;
      Logger.debug('ForgeWarden', 'Defeated');
    }
  }
}
